import React, { useEffect, useState, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { message } from 'antd';
import dayjs from 'dayjs';
import { HomeOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { useStore } from '../../hooks/storeContext';
import { useNotificationRefresh } from '../../contexts/NotificationContext';
import { usePageHeader } from '../../layouts/AdminLayout';
import PatientDetailsModal from './PatientDetailsModal';
import PatientMatchModal from '../../components/PatientQueue/PatientMatchModal';
import PatientEditView from './PatientEditView/index';
// AdminLayout now provided at router level
import PatientService from '../../services/PatientService';
import SettingsService from '../../services/SettingsService';
import VisitService from '../../services/VisitService';
import PatientFilters from './components/PatientFilters';
import PatientQueueTable from './components/PatientQueueTable';
import PatientQueueHeader from './components/PatientQueueHeader';
import {
  PageContainer,
  PatientQueueGlobalStyles,
  PatientEditContainer
} from './styled';

const PatientQueue = observer(() => {
  const { authStore } = useStore();
  const { registerRefreshCallback } = useNotificationRefresh();
  const { setPageHeader } = usePageHeader();
  const location = useLocation();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientDetailsVisible, setPatientDetailsVisible] = useState(false);
  const [patientMatchVisible, setPatientMatchVisible] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'completed', 'pre_registered', 'issues', 'archived'
  const [completedRetentionHours, setCompletedRetentionHours] = useState(() => {
    const saved = localStorage.getItem('bha_completed_retention_hours');
    return saved ? Number(saved) : 24;
  });
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    dob: null
  });
  
  const [patientCounts, setPatientCounts] = useState({
    active: 0,
    preRegistered: 0,
    completed: 0,
    archived: 0,
    needsAttention: 0,
    noShow: 0
  });
  
  // Status indicator settings
  const [statusSettings, setStatusSettings] = useState({
    timerEnabled: true,
    timerDuration: 2,
    yellowThreshold: 80
  });

  // Clear editing state when user navigates via breadcrumbs (location.key changes)
  const locationKeyRef = useRef(location.key);
  useEffect(() => {
    if (locationKeyRef.current !== location.key && editingPatient) {
      // User navigated (e.g., clicked breadcrumb) - clear editing state
      setEditingPatient(null);
    }
    locationKeyRef.current = location.key;
  }, [location.key, editingPatient]);

  // Update breadcrumb when editing a patient - show Home > Intake > Patient Name
  useEffect(() => {
    if (editingPatient) {
      const patientName = `${editingPatient.patient?.firstName || ''} ${editingPatient.patient?.lastName || ''}`.trim() || 'Member';
      setPageHeader({
        breadcrumbs: [
          { label: 'Home', path: '/admin/dashboard', icon: <HomeOutlined /> },
          { label: 'Intake', path: '/admin/member-queue' },
          { label: patientName }
        ]
      });
    } else {
      // Reset to default when back to queue view
      setPageHeader({
        breadcrumbs: null
      });
    }
  }, [editingPatient, setPageHeader]);
  
  // Ref to track current activeTab for polling (avoids stale closure)
  const activeTabRef = useRef(activeTab);
  const selectedEventRef = useRef(selectedEvent);
  const filtersRef = useRef(filters);
  const availableEventsRef = useRef(availableEvents);
  
  // Keep refs in sync with state
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);
  
  useEffect(() => {
    selectedEventRef.current = selectedEvent;
  }, [selectedEvent]);
  
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);
  
  useEffect(() => {
    availableEventsRef.current = availableEvents;
  }, [availableEvents]);

  // Register refresh callback for notifications
  useEffect(() => {
    const unregister = registerRefreshCallback(() => {
      refreshQueue();
    });
    
    return () => {
      unregister();
    };
  }, [registerRefreshCallback]);
  
  // Ref to hold the latest refresh function (avoids stale closure in WebSocket callbacks)
  const refreshQueueRef = useRef(null);
  
  // Debounce ref for WebSocket-triggered refreshes
  const refreshDebounceRef = useRef(null);
  // Track if a refresh is already in-flight to avoid stacking
  const refreshInFlightRef = useRef(false);
  
  // WebSocket connection ref
  const wsConnectionRef = useRef(null);

  // WebSocket-based real-time updates (replaces 10-second polling)
  useEffect(() => {
    let reconnectTimeout = null;
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 5;
    const RECONNECT_DELAY = 3000;

    const connectWebSocket = () => {
      // Clean up existing connection
      if (wsConnectionRef.current) {
        wsConnectionRef.current.close();
      }

      const connection = VisitService.connectToQueueWebSocket(
        // onMessage - handle incoming events
        (data) => {
          console.log('📡 Queue update received:', data.type);
          
          // Handle new visit created - add patient directly without full refresh
          if (data.type === 'queue:visitCreated' && data.data?.patient) {
            const visitData = data.data;
            const isPreReg = visitData.isPreRegistration || visitData.status === 'pre_registered';
            const currentTab = activeTabRef.current;
            
            // Map the websocket data to the expected patient queue structure
            const newPatient = {
              _id: visitData.visitId,
              visitId: visitData.visitId,
              patientId: visitData.patientId,
              status: visitData.status,
              checkInTime: visitData.checkInTime,
              timerStartTime: visitData.timerStartTime,
              queuePosition: visitData.queuePosition,
              isPreRegistration: visitData.isPreRegistration,
              createdAt: visitData.createdAt,
              patient: visitData.patient,
              eventInfo: visitData.eventInfo || {
                eventId: visitData.eventId,
                eventTitle: 'Mobile Screening',
                eventLocation: 'Location TBD'
              }
            };
            
            // Add to list if viewing the relevant tab
            if ((currentTab === 'active' && !isPreReg) || (currentTab === 'pre_registered' && isPreReg)) {
              setPatients(prev => {
                // Check if patient already exists (avoid duplicates)
                if (prev.some(p => p.visitId === newPatient.visitId || p._id === newPatient._id)) {
                  return prev;
                }
                // Calculate next queue position based on existing patients
                const maxPosition = prev.reduce((max, p) => Math.max(max, p.calculatedPosition || 0), 0);
                return [...prev, { ...newPatient, calculatedPosition: maxPosition + 1 }];
              });
            }
            
            // Always update counts
            calculatePatientCounts();
            return;
          }
          
          // For status changes, pre-registration activations, and visit updates - do full refresh
          // as these can move patients between tabs
          if ([
            'queue:statusChanged',
            'queue:preRegistrationActivated',
            'queue:visitUpdated'
          ].includes(data.type)) {
            if (refreshQueueRef.current) {
              refreshQueueRef.current();
            }
            return;
          }
          
          // For count refresh requests, just update counts
          if (data.type === 'queue:refreshCounts') {
            calculatePatientCounts();
          }
        },
        // onOpen
        () => {
          console.log('📡 Queue WebSocket connected');
          reconnectAttempts = 0; // Reset on successful connection
        },
        // onClose
        () => {
          console.log('📡 Queue WebSocket closed');
          // Attempt to reconnect with exponential backoff
          if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
            const delay = RECONNECT_DELAY * Math.pow(2, reconnectAttempts);
            console.log(`📡 Reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`);
            reconnectTimeout = setTimeout(() => {
              reconnectAttempts++;
              connectWebSocket();
            }, delay);
          } else {
            console.warn('📡 Max reconnection attempts reached, falling back to periodic refresh');
            // Fall back to polling every 30 seconds as backup
            reconnectTimeout = setInterval(() => {
              if (!document.hidden && refreshQueueRef.current) {
                refreshQueueRef.current();
              }
            }, 30000);
          }
        },
        // onError
        (error) => {
          console.error('📡 Queue WebSocket error:', error);
        }
      );

      wsConnectionRef.current = connection;
    };

    // Connect on mount
    connectWebSocket();

    // Handle visibility changes - reconnect when page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible - do an immediate refresh and ensure WebSocket is connected
        if (refreshQueueRef.current) {
          refreshQueueRef.current();
        }
        // Reconnect if not connected
        if (!wsConnectionRef.current || wsConnectionRef.current.ws.readyState !== WebSocket.OPEN) {
          reconnectAttempts = 0;
          connectWebSocket();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Cleanup on unmount
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        clearInterval(reconnectTimeout);
      }
      if (wsConnectionRef.current) {
        wsConnectionRef.current.close();
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Initial load
  useEffect(() => {
    const initializePage = async () => {
      await loadEvents();
      await Promise.all([loadPatients(), calculatePatientCounts(), loadStatusSettings()]);
    };
    initializePage();
  }, []);
  
  // Subsequent data updates
  useEffect(() => {
    loadPatients();
    calculatePatientCounts();
  }, [filters.status, filters.search, filters.dob, selectedEvent, activeTab, completedRetentionHours]);

  // Reload the events dropdown when switching tabs so it only lists events that
  // have patients in the newly selected tab (skip the initial mount — the page
  // initializer already loads events).
  const didMountEventsRef = useRef(false);
  useEffect(() => {
    if (!didMountEventsRef.current) {
      didMountEventsRef.current = true;
      return;
    }
    loadEvents();
  }, [activeTab]);

  const refreshQueue = useCallback(async () => {
    // Prevent overlapping refreshes
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    try {
      await Promise.all([loadPatients(), calculatePatientCounts()]);
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [activeTab, selectedEvent, filters]);
  
  // Debounced version for WebSocket-triggered refreshes (collapses rapid-fire events)
  const debouncedRefresh = useCallback(() => {
    if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
    refreshDebounceRef.current = setTimeout(() => {
      refreshQueue();
    }, 400);
  }, [refreshQueue]);
  
  // Keep refreshQueueRef in sync with the latest refreshQueue function
  useEffect(() => {
    refreshQueueRef.current = debouncedRefresh;
  });

  // Calculate patient counts for all tabs using dedicated count endpoint
  const calculatePatientCounts = async () => {
    try {
      const response = await PatientService.getQueueCounts();
      if (response.success && response.data) {
        setPatientCounts(response.data);
      }
    } catch (error) {
      console.error('Error calculating patient counts:', error);
      setPatientCounts({ active: 0, preRegistered: 0, completed: 0, archived: 0, needsAttention: 0, noShow: 0 });
    }
  };

  // Map a tab to the visit statuses it displays. Shared by the patient query and
  // the events dropdown so the dropdown only lists events with patients in the
  // current view (e.g. only events with patients in the intake queue).
  const getStatusFilterForTab = (tab) => {
    if (tab === 'active') return ['new', 'new_patient', 'in_progress', 'pending_review', 'matched', 'duplicate'];
    if (tab === 'pre_registered') return ['pre_registered'];
    if (tab === 'completed') return ['completed'];
    if (tab === 'issues') return ['needs_attention', 'no-show'];
    return undefined;
  };

  // Load available events for the filter dropdown
  const loadEvents = async () => {
    setLoadingEvents(true);
    try {
      const response = await PatientService.getAvailableEvents({
        tabFilter: getStatusFilterForTab(activeTab)
      });
      const events = response.data || [];
      
      // Events are already filtered to only include those with patients in the
      // current tab's statuses.
      setAvailableEvents(events);
    } catch (error) {
      console.error('Error loading events:', error);
      setAvailableEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };
  
  const loadPatients = async () => {
    setLoading(true);
    try {
      let allPatients = [];
      
      // Determine which statuses to show based on active tab
      const statusFilter = getStatusFilterForTab(activeTab);
      if (selectedEvent === 'all') {
        const filterParams = {
          status: filters.status !== 'all' ? filters.status : undefined,
          search: filters.search || undefined,
          dob: filters.dob && dayjs(filters.dob).isValid() ? dayjs(filters.dob).format('YYYY-MM-DD') : undefined,
          tabFilter: statusFilter,
          limit: 200
        };

        const response = await PatientService.getAllEventsPatientQueue(filterParams);
        
        allPatients = (response.data || []).map(visit => {
          const mappedPatient = {
            ...visit,
            // Map visit data to expected patient queue structure
            patient: {
              firstName: visit.firstName,
              lastName: visit.lastName,
              emailAddress: visit.emailAddress,
              phoneNumber: visit.phoneNumber,
              dateOfBirth: visit.dateOfBirth,
              patientId: visit.patient?.patientId || visit.patientId,
              usedBefore: visit.patient?.usedBefore || false,
              testingCode: visit.patient?.testingCode ?? visit.testingCode ?? ''
            },
            screeningType: visit.eventType?.name || 'health_screening',
            eventInfo: {
              eventId: visit.eventInfo?.eventId,
              eventTitle: visit.eventInfo?.eventTitle || 'Mobile Screening',
              eventLocation: visit.eventInfo?.eventLocation || 'Location TBD'
            }
          };
          
          return mappedPatient;
        });
        
      } else {
        const filterParams = {
          status: filters.status !== 'all' ? filters.status : undefined,
          search: filters.search || undefined,
          dob: filters.dob && dayjs(filters.dob).isValid() ? dayjs(filters.dob).format('YYYY-MM-DD') : undefined,
          tabFilter: statusFilter
        };
        
        const response = await PatientService.getPatientQueue(selectedEvent, filterParams);
        const selectedEventData = availableEvents.find(e => e._id === selectedEvent);
        allPatients = (response.data || []).map(visit => ({
          ...visit,
          // Map visit data to expected patient queue structure
          patient: {
            firstName: visit.firstName,
            lastName: visit.lastName,
            emailAddress: visit.emailAddress,
            phoneNumber: visit.phoneNumber,
            dateOfBirth: visit.dateOfBirth,
            patientId: visit.patient?.patientId || visit.patientId,
            usedBefore: visit.patient?.usedBefore || false,
            testingCode: visit.patient?.testingCode ?? visit.testingCode ?? ''
          },
          screeningType: visit.eventType?.name || selectedEventData?.type || 'health_screening',
          eventInfo: {
            eventId: selectedEvent,
            eventTitle: visit.eventInfo?.eventTitle || selectedEventData?.title || selectedEventData?.name,
            eventLocation: visit.eventInfo?.eventLocation || (typeof selectedEventData?.location === 'object'
              ? selectedEventData?.location?.name || selectedEventData?.location?.address
              : selectedEventData?.location || 'Location TBD')
          }
        }));
      }

      // Filter completed patients based on retention setting
      if (activeTab === 'completed') {
        const cutoff = new Date(Date.now() - completedRetentionHours * 60 * 60 * 1000);
        allPatients = allPatients.filter(patient => {
          const completedTime = new Date(patient.completionTime || patient.updatedAt);
          return completedTime >= cutoff;
        });
      }
        
      // Sort patients based on tab
      if (activeTab === 'active') {
        // Sort by priority and check-in time for active patients
        const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
        
        allPatients.sort((a, b) => {
          const aPriority = priorityOrder[a.priority || 'normal'] || 2;
          const bPriority = priorityOrder[b.priority || 'normal'] || 2;
          
          if (aPriority !== bPriority) {
            return bPriority - aPriority;
          }
          
          const aTime = new Date(a.checkInTime).getTime();
          const bTime = new Date(b.checkInTime).getTime();
          return aTime - bTime;
        });

        // Assign queue positions to active patients
        let queuePosition = 1;
        allPatients = allPatients.map(patient => ({
          ...patient, 
          calculatedPosition: queuePosition++
        }));
      } else {
        // Sort by completion/update time for completed/archived
        allPatients.sort((a, b) => {
          const aTime = new Date(a.completionTime || a.updatedAt).getTime();
          const bTime = new Date(b.completionTime || b.updatedAt).getTime();
          return bTime - aTime;
        });
      }
      
      setPatients(allPatients);
    } catch (error) {
      message.error('Failed to load patient queue');
      console.error('Error loading patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatusSettings = async () => {
    try {
      const response = await SettingsService.getQueueSettings();
      if (response.success && response.data) {
        setStatusSettings(response.data);
      }
    } catch (error) {
      console.error('Error loading status settings:', error);
      // Keep default settings if loading fails
    }
  };

  const handleStatusSettingsChange = useCallback(async (newSettings) => {
    try {
      const response = await SettingsService.updateQueueSettings(newSettings);
      if (response.success) {
        setStatusSettings(newSettings);
        message.success('Status settings saved successfully');
      } else {
        message.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving status settings:', error);
      message.error('Failed to save settings');
    }
  }, []);

  const handleMatchSuccess = () => {
    setPatientMatchVisible(false);
    setSelectedPatient(null);
    refreshQueue();
    message.success('Patient record updated successfully');
  };

  const handleViewDetails = (patient) => {
    setSelectedPatient(patient);
    setPatientDetailsVisible(true);
  };

  const handleEditPatient = async (patient) => {
    try {
      // Auto-update status to in_progress when editing starts (if not already)
      if (patient.status === 'new' || patient.status === 'new_patient' || patient.status === 'pending_review') {
        await PatientService.updateQueueStatus(patient._id, { 
          status: 'in_progress',
          startTime: new Date().toISOString()
        });
      }
      
      // Start the screening timer - only if patient was sent to bus AND screening hasn't started yet
      if (patient.intakeSentTime && !patient.screeningStartTime) {
        try {
          await VisitService.startScreening(patient._id);
          // Refresh the queue to show updated timer
          await refreshQueue();
        } catch (timingError) {
          // Don't block the screening process if timing fails
        }
      }
      
      setEditingPatient(patient);
      setPatientDetailsVisible(false);
    } catch (error) {
      console.error('Error updating patient status:', error);
      // Still allow editing even if status update fails
      setEditingPatient(patient);
      setPatientDetailsVisible(false);
    }
  };

  // Handle activating a pre-registered patient (moves to active queue and starts timer)
  const handleActivatePreRegistration = async (patient) => {
    try {
      const response = await VisitService.activatePreRegistration(patient._id);
      if (response.success) {
        message.success(`${patient.patient?.firstName || 'Patient'} activated and moved to active queue`);
        await refreshQueue();
        // Optionally switch to active tab to see the newly activated patient
        // setActiveTab('active');
      } else {
        message.error(response.message || 'Failed to activate pre-registration');
      }
    } catch (error) {
      console.error('Error activating pre-registration:', error);
      message.error('Failed to activate pre-registration');
    }
  };

  const handleStatusChange = async (patient, newStatus, notes = null) => {
    try {
      if (newStatus === 'needs_attention') {
        // Extract the correct eventId - it could be one of many shapes
        const eventId =
          patient.eventId?._id ||
          patient.eventId?.id ||
          patient.eventId ||
          patient.eventInfo?.eventId;
        
        // Use the markForAttention API for needs_attention status
        await PatientService.markForAttention(
          // pass actual patientId, not visit _id
          patient.patientId || patient.patient?.patientId,
          eventId,
          notes,
          patient._id // Visit Mongo _id used by /visits/:id/status
        );
        message.success('Patient marked as needs attention');
      } else {
        // Use the regular status update for other statuses
        const updateData = { 
          status: newStatus,
          ...(newStatus === 'completed' && { completionTime: new Date().toISOString() }),
          ...(newStatus === 'archived' && { archivedTime: new Date().toISOString() }),
          ...(newStatus === 'in_progress' && { startTime: new Date().toISOString() })
        };

        await PatientService.updateQueueStatus(patient._id, updateData);
        message.success(`Patient status updated to ${newStatus}`);
      }
      
      // Close the modal and refresh current tab
      setPatientDetailsVisible(false);
      setSelectedPatient(null);
      await refreshQueue();
    } catch (error) {
      console.error('Error updating patient status:', error);
      message.error('Failed to update patient status');
    }
  };

  // Quick-save a patient's testing code directly from the queue table row
  const handleSaveTestingCode = async (patient, testingCode) => {
    const patientId = patient.patient?.patientId || patient.patientId;
    if (!patientId) {
      message.error('Cannot save — missing patient ID');
      throw new Error('Missing patient ID');
    }
    try {
      await PatientService.updatePatient(patientId, { testingCode });
      message.success('Testing code saved');
      await refreshQueue();
      // Patch local state so the value persists even if the queue list omits it
      patchLocalTestingCode(patientId, testingCode);
    } catch (error) {
      console.error('Error saving testing code:', error);
      message.error('Failed to save testing code');
      throw error;
    }
  };

  // Patch a patient's testing code in the local queue state by patientId
  const patchLocalTestingCode = (patientId, testingCode) => {
    setPatients(prev => prev.map(entry => {
      const entryPatientId = entry.patient?.patientId || entry.patientId;
      if (entryPatientId !== patientId) return entry;
      return {
        ...entry,
        testingCode,
        patient: entry.patient ? { ...entry.patient, testingCode } : entry.patient
      };
    }));
  };

  // Called when the details modal saves a testing code — keep the table in sync
  const handleModalTestingCodeSaved = async (patientId, testingCode) => {
    await refreshQueue();
    patchLocalTestingCode(patientId, testingCode);
  };

  const handlePatientEditSuccess = async (updatedPatient, shouldComplete = false) => {
    try {
      if (shouldComplete) {
        message.success('Screening completed successfully');
      } else {
        message.success('Patient information updated successfully');
      }
      
      setEditingPatient(null);
      await refreshQueue();
    } catch (error) {
      console.error('Error in patient edit success handler:', error);
    }
  };

  const handlePatientMatch = async (patient) => {
    setSelectedPatient(patient);
    setPatientMatchVisible(true);
  };

  return (
    <>
      <PatientQueueGlobalStyles />
        {editingPatient ? (
          <PatientEditContainer>
            <PatientEditView
              patient={editingPatient}
              onBack={() => setEditingPatient(null)}
              onSuccess={handlePatientEditSuccess}
              showCompleteButton={true} // Enable complete screening button
            />
          </PatientEditContainer>
        ) : (
          <PageContainer>
            <PatientQueueHeader 
              activeTab={activeTab}
              onTabChange={setActiveTab}
              patientCounts={patientCounts}
              totalPatients={patients.length}
              loading={loading}
              onStatusSettingsChange={handleStatusSettingsChange}
              statusSettings={statusSettings}
              selectedEvent={selectedEvent}
            />
            
            <PatientFilters 
              filters={filters}
              onFiltersChange={setFilters}
              selectedEvent={selectedEvent}
              onEventChange={setSelectedEvent}
              availableEvents={availableEvents}
              loadingEvents={loadingEvents}
              activeTab={activeTab}
              completedRetentionHours={completedRetentionHours}
              onCompletedRetentionChange={(value) => {
                setCompletedRetentionHours(value);
                localStorage.setItem('bha_completed_retention_hours', String(value));
              }}
            />

            <div style={{ marginTop: '16px', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <PatientQueueTable 
                patients={patients}
                loading={loading}
                selectedEvent={selectedEvent}
                activeTab={activeTab}
                onViewDetails={handleViewDetails}
                onPatientMatch={handlePatientMatch}
                onRefresh={refreshQueue}
                onActivatePreRegistration={handleActivatePreRegistration}
                onSaveTestingCode={handleSaveTestingCode}
                isAdmin={authStore.isAdmin}
              />
            </div>
          
            <PatientDetailsModal
              visible={patientDetailsVisible}
              patient={selectedPatient}
              onCancel={() => {
                setPatientDetailsVisible(false);
                setSelectedPatient(null);
              }}
              onEditPatient={handleEditPatient}
              onStatusChange={handleStatusChange}
              onSaved={handleModalTestingCodeSaved}
            />

            <PatientMatchModal
              visible={patientMatchVisible}
              patient={selectedPatient}
              onCancel={() => {
                setPatientMatchVisible(false);
                setSelectedPatient(null);
              }}
              onMatchSuccess={handleMatchSuccess}
              eventId={selectedEvent !== 'all' ? selectedEvent : selectedPatient?.eventInfo?.eventId}
            />
          </PageContainer>
        )}
    </>
  );
});

export default PatientQueue;
